(() => {
  const widgetSelector = '#yotpo-reviews-main-widget';
  const modalSelector = '#yotpo-modal';
  const variantRowSelector = '.yotpo-review-product-variant-variant';
  const customerQuestionsSelector = '.yotpo-review-customer-questions-container';
  const reviewStyleId = 'athluna-yotpo-review-display-style';
  const observedWidgets = new WeakSet();

  function normalizeSizeLabels(widget) {
    widget.querySelectorAll(variantRowSelector).forEach((row) => {
      const title = row.querySelector('.yotpo-review-product-variant-title');
      const value = row.querySelector('.yotpo-review-product-variant-value');
      const originalLabel = title?.dataset.athlunaOriginalLabel
        || title?.textContent.trim().replace(/:\s*$/, '').toLowerCase();

      if (!title || !value || originalLabel !== 'size') {
        return;
      }

      row.classList.add('athluna-yotpo-purchased-size-variant');
      title.dataset.athlunaOriginalLabel = 'size';
      if (title.textContent.trim() !== 'Size Purchased:') {
        title.textContent = 'Size Purchased:';
      }

      const originalValue = value.textContent || '';
      const displayValue = originalValue.replace(/\s*\([^)]*\)\s*$/, '');

      if (displayValue !== originalValue) {
        value.textContent = displayValue;
      }
    });
  }

  function moveCustomerQuestionsToReviewContent(widget) {
    widget.querySelectorAll('[id^="yotpo-review-item-"]').forEach((reviewItem) => {
      const review = reviewItem.querySelector('.yotpo-review');
      const questions = review?.querySelector(customerQuestionsSelector);
      const content = review?.querySelector('.yotpo-review-center-panel');
      const thumbnails = content?.querySelector('.yotpo-review-thumbnail-container');

      if (!questions) return;

      if (content && thumbnails
        && (questions.parentElement !== content || questions.previousElementSibling !== thumbnails)) {
        thumbnails.insertAdjacentElement('afterend', questions);
      } else if (content && !thumbnails
        && (questions.parentElement !== content || questions !== content.lastElementChild)) {
        // Reviews without customer photos still need their fit details in the
        // review content column, rather than beside the reviewer name.
        content.append(questions);
      }

      const details = questions.querySelector('.yotpo-custom-questions-wrapper');
      if (details) formatCustomerQuestions(details);

      synchronizeOrderPurchasedSize(review, questions);
    });
  }

  function formatMobileCustomerQuestions(widget) {
    const preferredOrder = ['height', 'usual size', 'size purchased'];

    widget.querySelectorAll('.yotpo-review-questions-mobile-panel .yotpo-custom-questions-wrapper')
      .forEach((details) => {
        const panel = details.closest('.yotpo-review-questions-mobile-panel');
        const review = panel?.closest('.yotpo-review');
        const reviewItem = details.closest('[id^="yotpo-review-item-"]');
        const reviewContext = review || reviewItem;

        [...details.children]
          .filter((row) => row.classList.contains('yotpo-custom-questions-question-wrapper'))
          .forEach((row, index) => {
            const title = row.querySelector('.yotpo-custom-questions-title');
            if (!title) return;

            const key = title.textContent.trim().replace(/:\s*$/, '').toLowerCase();
            const order = preferredOrder.indexOf(key);
            const label = key === 'height' ? 'Height'
              : key === 'usual size' ? 'Usual Size'
                : key === 'size purchased' ? 'Size Purchased'
                  : title.textContent.trim().replace(/:\s*$/, '');

            const rowOrder = String(order < 0 ? preferredOrder.length + index : order);
            if (row.style.order !== rowOrder) row.style.order = rowOrder;
            if (title.textContent !== label + ':') title.textContent = label + ':';
          });

        if (reviewContext) synchronizeOrderPurchasedSize(reviewContext, details);
      });
  }

  function findOrderSizeVariantRow(review) {
    return [...review.querySelectorAll(variantRowSelector)].find((row) => {
      const title = row.querySelector('.yotpo-review-product-variant-title');
      const sourceLabel = title?.dataset.athlunaOriginalLabel
        || title?.textContent.trim().replace(/:\s*$/, '').toLowerCase();

      // Only accept Yotpo's native review variant row. Question answers use
      // different nodes and must never supply the displayed purchased size.
      return sourceLabel === 'size' || sourceLabel === 'size purchased';
    });
  }

  function synchronizeOrderPurchasedSize(review, questions) {
    const details = questions.matches('.yotpo-custom-questions-wrapper')
      ? questions
      : questions.querySelector('.yotpo-custom-questions-wrapper') || questions;
    const reviewItem = review.closest('[id^="yotpo-review-item-"]');
    const reviewRoot = reviewItem || review;
    const mobileDetails = reviewRoot.querySelector('.yotpo-review-questions-mobile-panel .yotpo-custom-questions-wrapper');
    const normalDetails = reviewRoot.querySelector('.yotpo-review-customer-questions-container .yotpo-custom-questions-wrapper');
    const targetDetails = window.matchMedia('(max-width: 767px)').matches
      ? mobileDetails || details
      : normalDetails || details;
    const variantRow = findOrderSizeVariantRow(reviewRoot);
    const questionScope = review.closest(widgetSelector) || review.closest(modalSelector) || reviewRoot;
    const questionRows = findCollectedPurchasedSizeRows(questionScope);
    const targetQuestionRow = findCollectedPurchasedSizeRow(targetDetails);
    const displayRows = [...reviewRoot.querySelectorAll('.athluna-yotpo-order-size-display-row')];
    if (!variantRow) {
      questionRows.forEach((row) => row.classList.add('athluna-yotpo-hide-collected-purchased-size'));
      displayRows.forEach((row) => row.classList.add('athluna-yotpo-hide-collected-purchased-size'));
      return;
    }

    const title = variantRow.querySelector('.yotpo-review-product-variant-title');
    const value = variantRow.querySelector('.yotpo-review-product-variant-value');
    const orderSize = value?.textContent.trim();
    if (!title || !orderSize) {
      questionRows.forEach((row) => row.classList.add('athluna-yotpo-hide-collected-purchased-size'));
      displayRows.forEach((row) => row.classList.add('athluna-yotpo-hide-collected-purchased-size'));
      return;
    }

    // Hide every collected answer and maintain one order-sourced row per review,
    // even when Yotpo renders separate desktop and mobile question containers.
    questionRows.forEach((row) => row.classList.add('athluna-yotpo-hide-collected-purchased-size'));
    const orderDisplayRow = displayRows[0] || createPurchasedSizeDisplayRow(targetDetails, targetQuestionRow);
    displayRows.slice(1).forEach((row) => row.classList.add('athluna-yotpo-hide-collected-purchased-size'));
    if (targetQuestionRow && orderDisplayRow.parentElement !== targetDetails) {
      targetQuestionRow.insertAdjacentElement('beforebegin', orderDisplayRow);
    } else if (targetQuestionRow && orderDisplayRow.nextElementSibling !== targetQuestionRow) {
      targetQuestionRow.insertAdjacentElement('beforebegin', orderDisplayRow);
    }
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    const purchasedSizeOrder = targetQuestionRow?.style.order || (isMobile ? '2' : '');
    if (orderDisplayRow.style.order !== purchasedSizeOrder) {
      // Desktop follows DOM order. The mobile panel uses flex order, so make
      // the purchased-size row third after Height and Usual Size.
      orderDisplayRow.style.order = purchasedSizeOrder;
    }

    const displayTitle = orderDisplayRow.querySelector('.yotpo-custom-questions-title');
    const displayValue = orderDisplayRow.querySelector('.yotpo-custom-questions-value');
    if (!displayTitle || !displayValue) return;

    orderDisplayRow.classList.remove('athluna-yotpo-hide-collected-purchased-size');
    orderDisplayRow.dataset.athlunaQuestionKey = 'size purchased';
    if (displayTitle.textContent !== 'Size Purchased:') displayTitle.textContent = 'Size Purchased:';
    if (displayValue.textContent !== orderSize) displayValue.textContent = orderSize;

    // Keep the source variant row in the DOM for repeated Yotpo renders, but
    // hide it so the order size appears only in the question's original slot.
    variantRow.classList.add('athluna-yotpo-order-size-source');
    [variantRow, variantRow.previousElementSibling, variantRow.nextElementSibling].forEach((element) => {
      if (element?.matches('.yotpo-variant-separator')) {
        element.classList.add('athluna-yotpo-hide-order-size-separator');
      }
      element?.querySelectorAll('.yotpo-variant-separator').forEach((separator) => {
        separator.classList.add('athluna-yotpo-hide-order-size-separator');
      });
    });

    const variantWrapper = reviewRoot.querySelector('.yotpo-review-product-variant-wrapper');
    const variantRows = [...(variantWrapper?.querySelectorAll(variantRowSelector) || [])];
    if (variantWrapper && variantRows.length && variantRows.every((row) =>
      row.classList.contains('athluna-yotpo-order-size-source')
        || row.classList.contains('athluna-yotpo-hide-color'))) {
      variantWrapper.classList.add('athluna-yotpo-compact-variant-wrapper');
    }
  }

  function findCollectedPurchasedSizeRow(questions) {
    const title = [...questions.querySelectorAll('.yotpo-custom-questions-title')]
      .find((candidate) => candidate.textContent.trim().replace(/:\s*$/, '').toLowerCase() === 'size purchased'
        && !candidate.closest('.athluna-yotpo-order-size-display-row'));
    return title?.closest('.yotpo-custom-questions-question-wrapper, .athluna-yotpo-question-row')
      || title?.parentElement;
  }

  function findCollectedPurchasedSizeRows(review) {
    const rows = new Set();
    review.querySelectorAll('.yotpo-custom-questions-title').forEach((title) => {
      if (title.textContent.trim().replace(/:\s*$/, '').toLowerCase() !== 'size purchased'
        || title.closest('.athluna-yotpo-order-size-display-row')) return;

      const details = title.closest('.yotpo-custom-questions-wrapper');
      let row = title.closest(
        '.yotpo-custom-questions-question-wrapper, .yotpo-review-question-wrapper, .yotpo-review-question, .athluna-yotpo-question-row',
      );
      if (!row && details) {
        row = title;
        while (row.parentElement && row.parentElement !== details) row = row.parentElement;
      }
      row ||= title.parentElement;
      if (row) rows.add(row);
    });
    return [...rows];
  }

  function findOrderPurchasedSizeDisplayRow(details) {
    return details.querySelector('.athluna-yotpo-order-size-display-row');
  }

  function createPurchasedSizeDisplayRow(details, questionRow) {
    const row = document.createElement('div');
    row.className = 'athluna-yotpo-question-row athluna-yotpo-order-size-display-row';
    row.dataset.athlunaQuestionKey = 'size purchased';

    const title = document.createElement('span');
    title.className = 'yotpo-custom-questions-title';
    const value = document.createElement('span');
    value.className = 'yotpo-custom-questions-value';
    row.append(title, value);

    if (questionRow?.parentElement) {
      questionRow.insertAdjacentElement('beforebegin', row);
      return row;
    }

    const usualSizeRow = [...details.querySelectorAll('.yotpo-custom-questions-question-wrapper, .athluna-yotpo-question-row')]
      .find((candidate) => candidate.querySelector('.yotpo-custom-questions-title')
        ?.textContent.trim().replace(/:\s*$/, '').toLowerCase() === 'usual size');
    const heightRow = [...details.querySelectorAll('.yotpo-custom-questions-question-wrapper, .athluna-yotpo-question-row')]
      .find((candidate) => candidate.querySelector('.yotpo-custom-questions-title')
        ?.textContent.trim().replace(/:\s*$/, '').toLowerCase() === 'height');

    (usualSizeRow || heightRow || details).insertAdjacentElement(usualSizeRow || heightRow ? 'afterend' : 'beforeend', row);
    return row;
  }

  function formatModalReviewDetails(widget) {
    widget.querySelectorAll('.yotpo-modal-body-right-side-center').forEach((review) => {
      const details = review.querySelector('.yotpo-custom-questions-wrapper');
      if (!details) return;

      formatCustomerQuestions(details);
      synchronizeOrderPurchasedSize(review, details);
    });
  }

  function formatCustomerQuestions(details) {
    const labelSelector = '.yotpo-custom-questions-title';
    const valueSelector = '.yotpo-custom-questions-value';
    const rows = [...details.querySelectorAll('.athluna-yotpo-question-row')];

    if (rows.length) {
      rows.forEach((row) => {
        const title = row.querySelector(labelSelector);
        const key = row.dataset.athlunaQuestionKey;
        const label = key === 'height' ? 'Height'
          : key === 'usual size' ? 'Usual Size'
            : key === 'size purchased' ? 'Size Purchased'
              : title?.dataset.athlunaQuestionLabel;

        if (title && label && title.textContent !== label + ':') title.textContent = label + ':';
      });
      return;
    }

    const titles = [...details.querySelectorAll(labelSelector)];
    const values = [...details.querySelectorAll(valueSelector)];
    const fields = titles.map((title, index) => {
      const originalLabel = title.textContent.trim().replace(/:\s*$/, '');
      const key = originalLabel.toLowerCase();
      const rowNumber = title.style.gridRowStart || String(index + 1);
      const value = values.find((candidate) => candidate.style.gridRowStart === rowNumber) || values[index];
      const label = key === 'height' ? 'Height'
        : key === 'usual size' ? 'Usual Size'
          : key === 'size purchased' ? 'Size Purchased'
            : originalLabel;

      return { title, value, key, label, rowNumber };
    }).filter((field) => field.value);

    if (!fields.length) return;

    const preferredOrder = ['height', 'usual size', 'size purchased'];
    fields.sort((a, b) => {
      const aOrder = preferredOrder.indexOf(a.key);
      const bOrder = preferredOrder.indexOf(b.key);
      return (aOrder < 0 ? preferredOrder.length : aOrder) - (bOrder < 0 ? preferredOrder.length : bOrder)
        || Number(a.rowNumber) - Number(b.rowNumber);
    });

    details.replaceChildren();
    fields.forEach(({ title, value, key, label }) => {
      const row = document.createElement('div');
      row.className = 'athluna-yotpo-question-row';
      row.dataset.athlunaQuestionKey = key;
      title.dataset.athlunaQuestionLabel = label;
      title.style.gridRowStart = '';
      title.style.gridColumn = '';
      value.style.gridRowStart = '';
      value.style.gridColumn = '';
      title.textContent = label + ':';
      row.append(title, value);
      details.append(row);
    });
  }

  function showFitValueBesideLabel(widget) {
    widget.querySelectorAll('.yotpo-custom-questions-range-question-wrapper').forEach((rating) => {
      const title = rating.querySelector('.yotpo-custom-questions-range-question-title');
      const value = rating.querySelector('.yotpo-custom-questions-range-question-value');
      const bar = rating.querySelector('.yotpo-progress-bar-wrapper');

      if (!title || !value) return;

      const label = title.textContent.trim().split(':')[0].trim();
      if (label.toLowerCase() !== 'fit') return;

      rating.classList.add('athluna-yotpo-fit-compact');
      rating.closest('.yotpo-review-question-wrapper')?.classList.add('athluna-yotpo-fit-question-compact');

      const fitValue = value.textContent.trim();
      if (!fitValue) return;
      const displayLabel = label.toLowerCase() === 'fit' ? 'Fit' : label;

      value.classList.add('athluna-yotpo-hide-fit-value');

      let inlineValue = title.querySelector('.athluna-yotpo-fit-inline-value');

      if (!inlineValue) {
        title.replaceChildren(document.createTextNode(`${label}: `));
        inlineValue = document.createElement('span');
        inlineValue.className = 'athluna-yotpo-fit-inline-value';
        inlineValue.style.display = 'inline';
        inlineValue.style.fontSize = 'inherit';
        inlineValue.style.fontWeight = '600';
        inlineValue.style.lineHeight = 'inherit';
        inlineValue.style.marginLeft = '3px';
        title.appendChild(inlineValue);
      }

      title.style.alignItems = 'baseline';
      const labelNode = title.firstChild;
      if (labelNode?.nodeType === Node.TEXT_NODE && labelNode.textContent !== displayLabel + ':') {
        labelNode.textContent = displayLabel + ':';
      }

      if (inlineValue.textContent !== fitValue) {
        inlineValue.textContent = fitValue;
      }

      if (bar) {
        bar.classList.add('athluna-yotpo-hide-fit-bar');
        rating.style.width = 'auto';
        rating.style.marginBottom = '0';
      }
    });
  }

  function applyYotpoDisplayStyles(widget) {
    if (!document.getElementById(reviewStyleId)) {
      const style = document.createElement('style');
      style.id = reviewStyleId;
      style.textContent = `
        #yotpo-reviews-main-widget .athluna-yotpo-hide-fit-value,
        #yotpo-modal .athluna-yotpo-hide-fit-value {
          display: none !important;
        }

        #yotpo-reviews-main-widget .athluna-yotpo-hide-fit-bar,
        #yotpo-modal .athluna-yotpo-hide-fit-bar {
          display: none !important;
        }

        #yotpo-reviews-main-widget .yotpo-review-thumbnail-container,
        #yotpo-modal .yotpo-review-thumbnail-container {
          margin: 0 18px 2px 0 !important;
        }

        #yotpo-reviews-main-widget .yotpo-review-customer-questions-container,
        #yotpo-modal .yotpo-review-customer-questions-container {
          margin: 8px 0 0 !important;
          order: 7 !important;
        }

        #yotpo-reviews-main-widget .yotpo-review-customer-questions-container {
          display: flex !important;
          flex-direction: column !important;
          align-items: flex-start !important;
          gap: 0 !important;
        }

        #yotpo-modal .yotpo-review-question-wrapper {
          margin-top: 8px !important;
        }

        #yotpo-reviews-main-widget .yotpo-review-questions-mobile-panel,
        #yotpo-modal .yotpo-review-questions-mobile-panel {
          margin-top: 0 !important;
        }

        .yotpo-display-s .yotpo-review-questions-mobile-panel .yotpo-review-question-wrapper {
          gap: 0 !important;
        }

        #yotpo-reviews-main-widget .yotpo-review-questions-mobile-panel {
          height: auto !important;
          max-height: none !important;
          overflow: visible !important;
        }

        #yotpo-reviews-main-widget .yotpo-review-questions-mobile-panel .yotpo-custom-questions-wrapper {
          display: flex !important;
          flex-direction: column !important;
          align-items: flex-start !important;
          gap: 0 !important;
          margin: 0 !important;
        }

        #yotpo-reviews-main-widget .yotpo-review-questions-mobile-panel .yotpo-custom-questions-question-wrapper {
          display: flex !important;
          align-items: baseline !important;
          gap: 3px !important;
          margin: 0 !important;
          order: 0;
        }

        #yotpo-reviews-main-widget .yotpo-review-questions-mobile-panel .athluna-yotpo-order-size-display-row,
        #yotpo-modal .yotpo-review-questions-mobile-panel .athluna-yotpo-order-size-display-row {
          display: flex !important;
          align-items: baseline !important;
          gap: 3px !important;
          margin: 0 !important;
          order: 0;
        }

        #yotpo-reviews-main-widget .yotpo-review-questions-mobile-panel .yotpo-custom-questions-title {
          margin: 0 !important;
          font-weight: 400 !important;
          text-transform: none !important;
        }

        #yotpo-reviews-main-widget .yotpo-review-questions-mobile-panel .yotpo-custom-questions-value {
          margin: 0 !important;
          font-weight: 600 !important;
        }

        #yotpo-reviews-main-widget .yotpo-review-questions-mobile-panel .yotpo-custom-questions-wrapper-suffix-padding {
          display: none !important;
        }

        #yotpo-reviews-main-widget .yotpo-review-customer-questions-container .yotpo-custom-questions-wrapper,
        #yotpo-modal .yotpo-review-customer-questions-container .yotpo-custom-questions-wrapper,
        #yotpo-modal .yotpo-modal-body-right-side-center .yotpo-custom-questions-wrapper {
          display: flex !important;
          flex-direction: column !important;
          gap: 0 !important;
          margin: 0 !important;
          align-items: flex-start;
          justify-content: flex-start;
        }

        #yotpo-reviews-main-widget .yotpo-review-customer-questions-container .yotpo-custom-questions-range-question-wrapper,
        #yotpo-reviews-main-widget .yotpo-review-questions-mobile-panel .yotpo-custom-questions-range-question-wrapper,
        #yotpo-reviews-main-widget .yotpo-review-customer-questions-container .yotpo-custom-questions-range-question,
        #yotpo-reviews-main-widget .yotpo-review-questions-mobile-panel .yotpo-custom-questions-range-question,
        #yotpo-reviews-main-widget .yotpo-review-question-wrapper,
        #yotpo-modal .yotpo-custom-questions-range-question-wrapper {
          margin: 0 !important;
          padding: 0 !important;
        }

        #yotpo-reviews-main-widget .yotpo-review-customer-questions-container .yotpo-custom-questions-range-question-title {
          margin: 0 !important;
          font-size: 13px !important;
          line-height: 18px !important;
        }

        #yotpo-modal .yotpo-modal-body-review-content {
          flex: 0 0 auto !important;
          height: auto !important;
          min-height: 0 !important;
          margin: 8px 0 0 !important;
        }

        #yotpo-reviews-main-widget .yotpo-review-customer-questions-container .athluna-yotpo-question-row,
        #yotpo-modal .yotpo-review-customer-questions-container .athluna-yotpo-question-row,
        #yotpo-modal .yotpo-modal-body-right-side-center .athluna-yotpo-question-row {
          display: flex;
          align-items: baseline;
          gap: 3px !important;
          font-size: 13px;
          line-height: 18px;
        }

        #yotpo-reviews-main-widget .athluna-yotpo-order-size-display-row,
        #yotpo-modal .athluna-yotpo-order-size-display-row {
          display: flex !important;
          align-items: baseline;
          gap: 3px !important;
          margin: 0 !important;
          font-size: 13px !important;
          line-height: 18px !important;
          color: #191919BF !important;
        }

        #yotpo-reviews-main-widget .athluna-yotpo-order-size-display-row .yotpo-custom-questions-title,
        #yotpo-modal .athluna-yotpo-order-size-display-row .yotpo-custom-questions-title {
          display: inline !important;
          margin: 0 !important;
          font-size: inherit !important;
          line-height: inherit !important;
          color: inherit !important;
          font-weight: 400 !important;
          text-transform: none !important;
        }

        #yotpo-reviews-main-widget .athluna-yotpo-order-size-display-row .yotpo-custom-questions-value,
        #yotpo-modal .athluna-yotpo-order-size-display-row .yotpo-custom-questions-value {
          display: inline !important;
          margin: 0 !important;
          font-size: inherit !important;
          line-height: inherit !important;
          color: inherit !important;
          font-weight: 600 !important;
        }

        #yotpo-reviews-main-widget .athluna-yotpo-purchased-size-variant,
        #yotpo-modal .athluna-yotpo-purchased-size-variant {
          display: flex !important;
          align-items: baseline !important;
          gap: 3px !important;
          font-size: 13px !important;
          line-height: 18px !important;
          color: #191919BF !important;
          margin-bottom: 0 !important;
        }

        #yotpo-reviews-main-widget .athluna-yotpo-fit-compact,
        #yotpo-modal .athluna-yotpo-fit-compact,
        #yotpo-reviews-main-widget .athluna-yotpo-fit-question-compact,
        #yotpo-modal .athluna-yotpo-fit-question-compact {
          flex: 0 0 auto !important;
          height: auto !important;
          min-height: 0 !important;
          margin: 0 !important;
          padding: 0 !important;
          gap: 0 !important;
        }

        #yotpo-reviews-main-widget .athluna-yotpo-purchased-size-variant .yotpo-review-product-variant-title,
        #yotpo-modal .athluna-yotpo-purchased-size-variant .yotpo-review-product-variant-title {
          color: inherit !important;
          font-size: inherit !important;
          line-height: inherit !important;
          margin: 0 !important;
          font-weight: 400 !important;
          text-transform: none !important;
        }

        #yotpo-reviews-main-widget .athluna-yotpo-purchased-size-variant .yotpo-review-product-variant-value,
        #yotpo-modal .athluna-yotpo-purchased-size-variant .yotpo-review-product-variant-value {
          color: inherit !important;
          font-size: inherit !important;
          line-height: inherit !important;
          margin: 0 !important;
          font-weight: 600 !important;
        }

        #yotpo-reviews-main-widget .yotpo-review-customer-questions-container .yotpo-custom-questions-title,
        #yotpo-reviews-main-widget .yotpo-review-customer-questions-container .yotpo-custom-questions-value,
        #yotpo-modal .yotpo-review-customer-questions-container .yotpo-custom-questions-title,
        #yotpo-modal .yotpo-review-customer-questions-container .yotpo-custom-questions-value,
        #yotpo-modal .yotpo-modal-body-right-side-center .yotpo-custom-questions-title,
        #yotpo-modal .yotpo-modal-body-right-side-center .yotpo-custom-questions-value {
          margin: 0 !important;
        }

        #yotpo-reviews-main-widget .athluna-yotpo-fit-inline-value,
        #yotpo-modal .athluna-yotpo-fit-inline-value {
          margin-left: 3px !important;
        }

        #yotpo-reviews-main-widget .yotpo-review-customer-questions-container .yotpo-custom-questions-title,
        #yotpo-modal .yotpo-review-customer-questions-container .yotpo-custom-questions-title,
        #yotpo-reviews-main-widget .yotpo-review-customer-questions-container .yotpo-custom-questions-value,
        #yotpo-modal .yotpo-review-customer-questions-container .yotpo-custom-questions-value,
        #yotpo-modal .yotpo-modal-body-right-side-center .yotpo-custom-questions-title,
        #yotpo-modal .yotpo-modal-body-right-side-center .yotpo-custom-questions-value {
          font-size: inherit !important;
          line-height: inherit !important;
          color: inherit !important;
        }

        #yotpo-reviews-main-widget .yotpo-review-customer-questions-container .yotpo-custom-questions-title,
        #yotpo-modal .yotpo-review-customer-questions-container .yotpo-custom-questions-title {
          font-weight: 400 !important;
          text-transform: none !important;
        }

        #yotpo-modal .yotpo-modal-body-right-side-center .yotpo-custom-questions-title {
          font-weight: 400 !important;
          text-transform: none !important;
        }

        #yotpo-reviews-main-widget .yotpo-review-customer-questions-container .yotpo-custom-questions-value,
        #yotpo-modal .yotpo-review-customer-questions-container .yotpo-custom-questions-value,
        #yotpo-modal .yotpo-modal-body-right-side-center .yotpo-custom-questions-value,
        #yotpo-reviews-main-widget .yotpo-review-questions-mobile-panel .yotpo-custom-questions-range-question-title .athluna-yotpo-fit-inline-value,
        #yotpo-modal .yotpo-review-questions-mobile-panel .yotpo-custom-questions-range-question-title .athluna-yotpo-fit-inline-value,
        #yotpo-modal .yotpo-modal-body-right-side-center .yotpo-custom-questions-range-question-title .athluna-yotpo-fit-inline-value {
          font-weight: 600 !important;
        }

        #yotpo-reviews-main-widget .yotpo-review-questions-mobile-panel .yotpo-custom-questions-range-question-title,
        #yotpo-modal .yotpo-review-questions-mobile-panel .yotpo-custom-questions-range-question-title,
        #yotpo-modal .yotpo-modal-body-right-side-center .yotpo-custom-questions-range-question-title {
          font-weight: 400 !important;
          color: inherit !important;
        }

        #yotpo-reviews-main-widget .athluna-yotpo-fit-inline-value,
        #yotpo-modal .athluna-yotpo-fit-inline-value {
          color: inherit !important;
        }

        #yotpo-reviews-main-widget .yotpo-review-customer-questions-container .yotpo-custom-questions-title,
        #yotpo-reviews-main-widget .yotpo-review-customer-questions-container .yotpo-custom-questions-value,
        #yotpo-reviews-main-widget .yotpo-review-questions-mobile-panel .yotpo-custom-questions-title,
        #yotpo-reviews-main-widget .yotpo-review-questions-mobile-panel .yotpo-custom-questions-value,
        #yotpo-reviews-main-widget .yotpo-review-questions-mobile-panel .yotpo-custom-questions-range-question-title,
        #yotpo-reviews-main-widget .yotpo-review-questions-mobile-panel .athluna-yotpo-fit-inline-value,
        #yotpo-modal .yotpo-review-customer-questions-container .yotpo-custom-questions-title,
        #yotpo-modal .yotpo-review-customer-questions-container .yotpo-custom-questions-value,
        #yotpo-modal .yotpo-review-questions-mobile-panel .yotpo-custom-questions-title,
        #yotpo-modal .yotpo-review-questions-mobile-panel .yotpo-custom-questions-value,
        #yotpo-modal .yotpo-review-questions-mobile-panel .yotpo-custom-questions-range-question-title,
        #yotpo-modal .yotpo-review-questions-mobile-panel .athluna-yotpo-fit-inline-value,
        #yotpo-modal .yotpo-modal-body-right-side-center .yotpo-custom-questions-title,
        #yotpo-modal .yotpo-modal-body-right-side-center .yotpo-custom-questions-value,
        #yotpo-modal .yotpo-modal-body-right-side-center .yotpo-custom-questions-range-question-title,
        #yotpo-modal .yotpo-modal-body-right-side-center .athluna-yotpo-fit-inline-value {
          color: #191919BF !important;
        }

        #yotpo-modal .yotpo-custom-questions-wrapper-suffix-padding {
          display: none !important;
        }

        #yotpo-reviews-main-widget .yotpo-review-questions-mobile-panel .yotpo-custom-questions-range-question-title,
        #yotpo-modal .yotpo-review-questions-mobile-panel .yotpo-custom-questions-range-question-title,
        #yotpo-modal .yotpo-modal-body-right-side-center .yotpo-custom-questions-range-question-title {
          font-size: 13px !important;
          line-height: 18px !important;
        }

        #yotpo-modal .yotpo-modal-body-right-side-center .yotpo-custom-questions-range-question {
          margin: 0 !important;
        }

        .athluna-yotpo-hide-collected-purchased-size {
          display: none !important;
        }

        #yotpo-reviews-main-widget .yotpo-review-questions-mobile-panel .yotpo-custom-questions-question-wrapper.athluna-yotpo-hide-collected-purchased-size,
        #yotpo-modal .yotpo-review-questions-mobile-panel .yotpo-custom-questions-question-wrapper.athluna-yotpo-hide-collected-purchased-size,
        #yotpo-reviews-main-widget .yotpo-review-customer-questions-container .athluna-yotpo-hide-collected-purchased-size,
        #yotpo-modal .yotpo-review-customer-questions-container .athluna-yotpo-hide-collected-purchased-size {
          display: none !important;
        }

        #yotpo-reviews-main-widget .athluna-yotpo-order-size-source,
        #yotpo-modal .athluna-yotpo-order-size-source {
          display: none !important;
        }

        #yotpo-reviews-main-widget .athluna-yotpo-compact-variant-wrapper,
        #yotpo-modal .athluna-yotpo-compact-variant-wrapper,
        #yotpo-reviews-main-widget .athluna-yotpo-hide-order-size-separator,
        #yotpo-modal .athluna-yotpo-hide-order-size-separator {
          display: none !important;
        }

        #yotpo-reviews-main-widget .athluna-yotpo-hide-color,
        #yotpo-modal .athluna-yotpo-hide-color,
        #yotpo-reviews-main-widget .athluna-yotpo-hide-color-separator,
        #yotpo-modal .athluna-yotpo-hide-color-separator {
          display: none !important;
        }

        @media screen and (max-width: 767px) {
          #yotpo-reviews-main-widget .yotpo-review-thumbnail-container {
            margin-top: 8px !important;
          }

          #yotpo-reviews-main-widget .yotpo-review-center-panel > .yotpo-crfs-seperator {
            display: none !important;
            margin: 0 !important;
          }

          #yotpo-reviews-main-widget .yotpo-review-center-panel > .yotpo-review-product-variant-wrapper {
            margin-top: 8px !important;
          }

          #yotpo-modal .yotpo-review-question-wrapper {
            gap: 0 !important;
          }

          #yotpo-modal .yotpo-modal-body-right-side-center .yotpo-custom-questions-range-question-wrapper {
            margin: 0 !important;
          }

          #yotpo-modal .yotpo-modal-body-right-side-center .yotpo-modal-body-review-media-container {
            margin-top: 8px !important;
          }

        }

        @media screen and (min-width: 768px) {
          #yotpo-modal .yotpo-modal-body-right-side-center {
            display: flex !important;
            flex-direction: column !important;
            justify-content: flex-start !important;
            align-content: flex-start !important;
            height: auto !important;
            min-height: 0 !important;
          }

          #yotpo-modal .yotpo-modal-body-right-side-center .yotpo-review-question-wrapper {
            flex: 0 0 auto !important;
            margin-top: 15px !important;
            align-self: flex-start !important;
          }

          #yotpo-modal .yotpo-modal-body-right-side-center .yotpo-modal-body-review-content,
          #yotpo-modal .yotpo-modal-body-right-side-center .yotpo-modal-body-review-media-container {
            flex: 0 0 auto !important;
            height: auto !important;
            min-height: 0 !important;
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
          }

          #yotpo-reviews-main-widget .yotpo-review {
            margin-top: 30px !important;
            margin-bottom: 30px !important;
          }

          #yotpo-reviews-main-widget .yotpo-review-customer-questions-container {
            margin-top: 15px !important;
          }

          #yotpo-modal .yotpo-review-question-wrapper {
            margin-top: 15px !important;
          }

          #yotpo-reviews-main-widget .yotpo-review-thumbnail-container {
            margin-top: 8px !important;
            margin-bottom: 0 !important;
          }
        }
      `;
      document.head.appendChild(style);
    }

    widget.querySelectorAll(variantRowSelector).forEach((row) => {
      const title = row.querySelector('.yotpo-review-product-variant-title');
      if (title?.textContent.trim().replace(/:\s*$/, '').toLowerCase() !== 'color') return;

      row.classList.add('athluna-yotpo-hide-color');
      [row, row.previousElementSibling, row.nextElementSibling].forEach((element) => {
        if (element?.matches('.yotpo-variant-separator')) {
          element.classList.add('athluna-yotpo-hide-color-separator');
        }
        element?.querySelectorAll('.yotpo-variant-separator').forEach((separator) => {
          separator.classList.add('athluna-yotpo-hide-color-separator');
        });
      });

      const variantWrapper = row.closest('.yotpo-review-product-variant-wrapper');
      const variants = [...(variantWrapper?.querySelectorAll(variantRowSelector) || [])];
      if (variants.length && variants.every((variant) =>
        variant.classList.contains('athluna-yotpo-order-size-source')
          || variant.classList.contains('athluna-yotpo-hide-color'))) {
        variantWrapper.classList.add('athluna-yotpo-compact-variant-wrapper');
      }
    });
  }

  function normalizeReviewDetails(widget) {
    normalizeSizeLabels(widget);
    moveCustomerQuestionsToReviewContent(widget);
    formatMobileCustomerQuestions(widget);
    formatModalReviewDetails(widget);
    showFitValueBesideLabel(widget);
    applyYotpoDisplayStyles(widget);
  }

  function observeWidget(widget) {
    if (observedWidgets.has(widget)) return;

    observedWidgets.add(widget);
    normalizeReviewDetails(widget);

    const observer = new MutationObserver(() => normalizeReviewDetails(widget));
    observer.observe(widget, { childList: true, characterData: true, subtree: true });
  }

  function observeReviewWidgets() {
    document.querySelectorAll(`${widgetSelector}, ${modalSelector}`).forEach(observeWidget);
  }

  observeReviewWidgets();

  const pageObserver = new MutationObserver(observeReviewWidgets);
  pageObserver.observe(document.documentElement, { childList: true, subtree: true });
})();
